import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ShieldCheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

function VerifyAdmin() {
  const location = useLocation();
  const navigate = useNavigate();
  const { verifyMagicLink, user } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get('token');

    if (!token) {
      setStatus('error');
      setMessage('No verification token found in URL');
      return;
    }

    const handleVerification = async () => {
      try {
        setStatus('verifying');
        await verifyMagicLink(token);
        setStatus('success');
        setMessage('Admin authentication successful! Redirecting to admin panel...');
        
        // Redirect to admin panel after successful verification
        setTimeout(() => {
          navigate('/admin');
        }, 2000);
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.detail || 'Admin verification failed');
      }
    };

    handleVerification();
  }, [location.search, verifyMagicLink, navigate]);

  const getStatusIcon = () => {
    switch (status) {
      case 'verifying':
        return (
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        );
      case 'success':
        return (
          <ShieldCheckIcon className="h-12 w-12 text-green-600" />
        );
      case 'error':
        return (
          <ExclamationTriangleIcon className="h-12 w-12 text-red-600" />
        );
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'verifying':
        return 'text-gray-600 dark:text-gray-400';
      case 'success':
        return 'text-green-600 dark:text-green-400';
      case 'error':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getStatusTitle = () => {
    switch (status) {
      case 'verifying':
        return 'Verifying Admin Access...';
      case 'success':
        return 'Admin Access Granted';
      case 'error':
        return 'Admin Verification Failed';
      default:
        return 'Processing...';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white dark:bg-dark-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Security Warning Banner */}
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <ShieldCheckIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Admin Security Verification
                </h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  <p>You are accessing administrative functions with elevated privileges.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Status Display */}
          <div className="text-center">
            <div className="flex justify-center mb-4">
              {getStatusIcon()}
            </div>
            
            <h2 className={`text-xl font-bold ${getStatusColor()}`}>
              {getStatusTitle()}
            </h2>
            
            {message && (
              <p className={`mt-2 text-sm ${getStatusColor()}`}>
                {message}
              </p>
            )}

            {status === 'success' && (
              <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-md">
                <p className="text-sm text-green-700 dark:text-green-300">
                  Welcome back, Administrator! You now have full access to the admin panel.
                </p>
              </div>
            )}

            {status === 'error' && (
              <div className="mt-6 space-y-4">
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-md">
                  <p className="text-sm text-red-700 dark:text-red-300">
                    Admin verification failed. This could be due to:
                  </p>
                  <ul className="mt-2 text-sm text-red-600 dark:text-red-400 list-disc list-inside">
                    <li>Expired or invalid token</li>
                    <li>Insufficient admin privileges</li>
                    <li>Network connectivity issues</li>
                  </ul>
                </div>
                
                <button
                  onClick={() => navigate('/login')}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Return to Login
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default VerifyAdmin;
